import { FC, useMemo, lazy, Suspense } from "react";

import { CriteriaCheckSettings } from "types/application";
const ScoreGraph = lazy(
  () =>
    import("views/applications-id/components/ApplicationScoreCard/ScoreGraph")
);
import { ScoreGraphSize } from "./constants";
import { ScreeningCriteriaType } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import {
  ApplicationStatus,
  DecisionAction,
  RequiresOverridesIdentityVerification
} from "shared/types/application";
import { Card } from "shared/components/Card";
import { useAuthContext } from "providers/Auth";
import { RecommendationButton } from "../RecommendationButton";
import { toFixedFloored } from "utils/toFixedFloored";
import { canDecisionBeMade } from "utils/canMakeDecision";
import { Loader } from "shared/components/Loader";
type Props = {
  applicationScore?: number | null;
  recommendation?: DecisionAction;
  onButtonClick?: () => void;
  criteriaCheckSettings?: CriteriaCheckSettings;
  applicationSubmitted: boolean;
  templateCriteriaSettings?: ScreeningCriteriaType;
  status?: ApplicationStatus;
  applicants?: any;
  requires_overrides_identity_verification?: RequiresOverridesIdentityVerification;
  requirements?: string[];
};

export const ApplicationScoreCard: FC<Props> = ({
  applicationScore,
  recommendation,
  onButtonClick,
  applicationSubmitted,
  templateCriteriaSettings,
  status,
  applicants,
  requires_overrides_identity_verification,
  requirements
}) => {
  const { permissions, type } = useAuthContext();

  const { decline_range_end, conditional_approval_range_end } =
    templateCriteriaSettings || {};

  const score = !applicationScore
    ? 0
    : applicationScore === 10
      ? 10
      : toFixedFloored(applicationScore, 1);

  const canMakeDecision = useMemo(() => {
    return canDecisionBeMade({
      applicants,
      status: status || ApplicationStatus.Started,
      type,
      permissions,
      requirements
    });
  }, [
    requires_overrides_identity_verification,
    applicants,
    status,
    type,
    permissions,
    requirements
  ]);

  return (
    <Card
      // Flex column is needed for button
      className="w-[300px] min-h-[274px] p-6 flex flex-col max-h-[340px] h-full"
      onClick={
        applicationSubmitted && !recommendation ? onButtonClick : undefined
      }
    >
      <div className="flex h-[136px] justify-center items-center mt-8">
        <Suspense fallback={<Loader />}>
          <ScoreGraph
            size={ScoreGraphSize.LG}
            score={applicationScore}
            recommendation={recommendation}
            declineRangeEnd={decline_range_end}
            conditionalApprovalRangeEnd={conditional_approval_range_end}
          />
        </Suspense>
      </div>

      <p className="mt-auto text-center text-xs text-content-300 font-medium pt-4 border-t border-alpha-40 -translate-y-6">
        Based on the application score of {score}, we recommend
        {recommendation === DecisionAction.Approve
          ? " approval"
          : recommendation === DecisionAction.Decline
            ? " declining"
            : " conditional approval"}
      </p>

      <RecommendationButton
        recommendation={recommendation!}
        onClickMakeDecision={onButtonClick!}
        canMakeDecision={canMakeDecision}
      />
    </Card>
  );
};
